import React, { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, KeyRound, Eye, EyeOff, Camera, Upload, MessageSquare } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { Avatar } from "@/components/Avatar";
import CompanySettingsCard from "@/components/CompanySettingsCard";
import InvoiceTemplatesSection from "@/components/InvoiceTemplatesSection";
import ServiceTypesCard from "@/components/ServiceTypesCard";
import { NotificationPrefsCard, BroadcastBannerCard } from "@/components/NotificationSettingsCards";

// Resize any image to a square 300×300 JPEG (data URL, ~<100 KB) before upload.
// Keeps the backend 600 KB cap comfortable and hides picture size from the user.
async function _resizeImage(file, size = 300) {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  // Center-crop to square
  const short = Math.min(bitmap.width, bitmap.height);
  const sx = (bitmap.width - short) / 2;
  const sy = (bitmap.height - short) / 2;
  ctx.drawImage(bitmap, sx, sy, short, short, 0, 0, size, size);
  return canvas.toDataURL("image/jpeg", 0.86);
}

function TemplatesWrapper() {
  const [tokens, setTokens] = useState(null);
  useEffect(() => {
    api.get("/api/settings/invoice-templates/tokens").then((r) => setTokens(r.data)).catch(() => {});
  }, []);
  return <InvoiceTemplatesSection tokens={tokens} />;
}

export default function Settings() {
  const [cleaning, setCleaning] = useState(false);
  const [result, setResult] = useState(null);
  const [search] = useSearchParams();
  const { setUser, user } = useAuth();
  const passwordCardRef = useRef(null);

  // Change-my-password state
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  // Profile picture + bio state
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileRef = useRef(null);
  const [bio, setBio] = useState(user?.bio || "");
  const [savingBio, setSavingBio] = useState(false);
  const isAdmin = hasRole(user, "admin");

  useEffect(() => { setBio(user?.bio || ""); }, [user?.bio]);

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setUploadingAvatar(true);
    try {
      const dataUrl = await _resizeImage(file, 300);
      const { data } = await api.post("/api/auth/me/avatar", { avatar_url: dataUrl });
      setUser && setUser({ ...user, avatar_url: data.avatar_url });
      toast.success("Profile picture updated");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update picture"));
    } finally {
      setUploadingAvatar(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAvatar = async () => {
    setUploadingAvatar(true);
    try {
      const { data } = await api.post("/api/auth/me/avatar", { avatar_url: "" });
      setUser && setUser({ ...user, avatar_url: data.avatar_url || null });
      toast.success("Profile picture removed");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not remove picture"));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const saveBio = async () => {
    setSavingBio(true);
    try {
      const { data } = await api.post("/api/auth/me/bio", { bio });
      setUser && setUser({ ...user, bio: data.bio || "" });
      toast.success(bio ? "Bio saved" : "Bio cleared");
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not save bio"));
    } finally {
      setSavingBio(false);
    }
  };

  // Auto-scroll + highlight when arriving via the nav nudge.
  useEffect(() => {
    if (search.get("action") === "change-password" && passwordCardRef.current) {
      passwordCardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      passwordCardRef.current.classList.add("ring-2", "ring-amber-300");
      const t = setTimeout(() => {
        passwordCardRef.current?.classList.remove("ring-2", "ring-amber-300");
      }, 2600);
      return () => clearTimeout(t);
    }
  }, [search]);

  const cleanup = async () => {
    if (!window.confirm("This will PERMANENTLY delete all sample / imported / TEST-prefixed clients, projects, invoices and payments. Continue?")) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/api/admin/cleanup-test-data");
      setResult(data);
      toast.success("Test data cleaned");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setCleaning(false);
    }
  };

  const changeMyPassword = async () => {
    const nw = (newPwd || "").trim();
    if (nw.length < 8) return toast.error("New password must be at least 8 characters");
    if (nw !== (confirmPwd || "").trim()) return toast.error("New passwords do not match");
    if (currentPwd && currentPwd === nw) return toast.error("New password must be different from your current one");
    setSavingPwd(true);
    try {
      await api.post("/api/auth/change-password", { new_password: nw });
      toast.success("Password updated — sign in fresh next time.");
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd(""); setShowPwd(false);
      // Clear the seed-password flag locally so the nudge disappears immediately.
      if (user && setUser) setUser({ ...user, is_seed_password: false });
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not update password."));
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Application preferences and configuration.</p>
      </div>
      <Card className="border-slate-200 shadow-none p-6">
        <div className="text-sm text-slate-600">
          <div className="font-medium mb-2">Organization</div>
          <div className="grid grid-cols-2 gap-3 text-slate-500">
            <div>Name</div><div className="text-slate-900">Tecticonism Studio LLC</div>
            <div>Default currency</div><div className="text-slate-900">USD</div>
            <div>Email notifications</div><div className="text-slate-900">Disabled</div>
            <div>Object storage</div><div className="text-slate-900">Emergent object store — active</div>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-none p-6" data-testid="profile-picture-card">
        <div className="font-heading font-medium mb-1 flex items-center gap-2">
          <Camera className="w-4 h-4 text-slate-500" />
          Profile picture
        </div>
        <div className="text-sm text-slate-500 mb-4">
          Shown on the sidebar, Team directory and the "Welcome back" screen at sign-in.
        </div>
        <div className="flex items-start gap-4 flex-wrap">
          <Avatar src={user?.avatar_url} name={user?.name} size="w-20 h-20" data-testid="profile-avatar-preview" />
          <div className="space-y-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickAvatar} data-testid="profile-avatar-input" />
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploadingAvatar} data-testid="profile-avatar-upload">
                <Upload className="w-3.5 h-3.5 mr-1.5" />
                {user?.avatar_url ? "Change picture" : "Upload picture"}
              </Button>
              {user?.avatar_url && (
                <Button variant="outline" size="sm" onClick={removeAvatar} disabled={uploadingAvatar} data-testid="profile-avatar-remove" className="text-rose-600 hover:text-rose-700">
                  Remove
                </Button>
              )}
            </div>
            <div className="text-xs text-slate-500">Auto-resized to 300×300 JPEG on your device. Max 600 KB after resize.</div>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-none p-6" data-testid="bio-card">
        <div className="font-heading font-medium mb-1 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-slate-500" />
          Personal bio
        </div>
        <div className="text-sm text-slate-500 mb-4">
          A short line about yourself — appears on the Team directory card. Max 280 characters.
        </div>
        <Textarea value={bio} onChange={(e) => setBio(e.target.value.slice(0, 280))} rows={3} placeholder="e.g. Loves numbers and long walks between spreadsheets." data-testid="profile-bio-input" />
        <div className="flex items-center justify-between mt-2">
          <div className={`text-xs ${bio.length >= 260 ? "text-amber-700" : "text-slate-500"}`} data-testid="bio-counter">{bio.length} / 280</div>
          <Button size="sm" onClick={saveBio} disabled={savingBio} className="bg-brand hover:bg-brand-hover" data-testid="profile-bio-save">
            {savingBio ? "Saving…" : "Save bio"}
          </Button>
        </div>
      </Card>

      <Card ref={passwordCardRef} className="border-slate-200 shadow-none p-6 transition-shadow">
        <div className="font-heading font-medium mb-1 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-slate-500" />
          Change my password
        </div>
        <div className="text-sm text-slate-500 mb-4">
          Rotate your own sign-in password. Choose something at least 8 characters that you haven't used elsewhere.
        </div>
        {user?.is_seed_password && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-xs" data-testid="seed-password-warning">
            Your account is still using the seed password from initial setup. Please pick a permanent one below.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 max-w-2xl">
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Current password <span className="text-slate-400">(optional but recommended)</span></Label>
            <Input
              type={showPwd ? "text" : "password"}
              value={currentPwd}
              onChange={(e) => setCurrentPwd(e.target.value)}
              placeholder="Type your current password"
              autoComplete="current-password"
              data-testid="settings-current-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">New password (min 8)</Label>
            <div className="relative">
              <Input
                type={showPwd ? "text" : "password"}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Choose a strong password"
                autoComplete="new-password"
                className="pr-9"
                data-testid="settings-new-password"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                title={showPwd ? "Hide" : "Show"}
              >
                {showPwd ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Confirm new password</Label>
            <Input
              type={showPwd ? "text" : "password"}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              placeholder="Re-type the new password"
              autoComplete="new-password"
              data-testid="settings-confirm-password"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={changeMyPassword}
            disabled={savingPwd || !newPwd || !confirmPwd}
            className="bg-brand hover:bg-brand-hover"
            data-testid="settings-save-password"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            {savingPwd ? "Saving…" : "Update my password"}
          </Button>
        </div>
      </Card>

      {isAdmin && <CompanySettingsCard />}

      <NotificationPrefsCard />

      {isAdmin && <BroadcastBannerCard />}

      {isAdmin && <ServiceTypesCard />}

      {isAdmin && <TemplatesWrapper />}

      {isAdmin && (
      <Card className="border-slate-200 shadow-none p-6">
        <div className="font-heading font-medium mb-1">Test Data Cleanup</div>
        <div className="text-sm text-slate-500 mb-4">Permanently removes records flagged as sample data, imported via CSV, or with TEST/CANARY prefixes across clients, projects, invoices, payments and payment stages. Irreversible.</div>
        <Button variant="outline" onClick={cleanup} disabled={cleaning} data-testid="cleanup-test-data-btn" className="text-rose-600 border-rose-200 hover:bg-rose-50">
          <Trash2 className="w-4 h-4 mr-2" />{cleaning ? "Cleaning…" : "Clean test data"}
        </Button>
        {result && (
          <div className="mt-4 text-sm text-slate-700 border-t border-slate-100 pt-3">
            <div>Removed: {result.clients} clients · {result.projects} projects · {result.invoices} invoices · {result.payments} payments · {result.payment_stages} payment stages · {result.notifications} notifications.</div>
          </div>
        )}
      </Card>
      )}
    </div>
  );
}
